import { useState } from 'react';
import { useLanguage } from '../../../hooks/useLanguage';

export default function FAQNew() {
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const { language } = useLanguage();

  const toggle = (key: string) => setOpenIndex(prev => (prev === key ? null : key));

  const content = {
    de: {
      badge: 'FAQ',
      headline: 'Häufig gestellte Fragen',
      subtext: 'Alles, was Sie über Implementierung, Sicherheit und Preisgestaltung von TELIQone wissen müssen.',
      categories: [
        {
          category: 'Implementierung',
          icon: 'ri-settings-3-line',
          items: [
            {
              q: 'Wie lange dauert die Implementierung?',
              a: 'Ein typisches TELIQone-Rollout dauert 6–10 Wochen vom Kickoff bis zum Go-live. Dies umfasst Systemintegration, Prozess-Mapping, Feinabstimmung der Modelle auf Ihre Telco-Daten sowie Agentenschulung. Wir stellen Ihnen während der gesamten Implementierung ein dediziertes Team zur Seite.',
            },
            {
              q: 'Mit welchen Systemen integriert sich TELIQone?',
              a: 'TELIQone verbindet sich nativ mit führenden BSS/OSS-Plattformen (z. B. Amdocs, Comverse, Ericsson), CRM-Systemen (Salesforce, SAP), Ticketing-Tools (ServiceNow, Jira) sowie jeder REST/SOAP-API. Individuelle Konnektoren sind auf Anfrage erhältlich.',
            },
            {
              q: 'Müssen wir unsere bestehende Infrastruktur ersetzen?',
              a: 'Nein. TELIQone ist als nicht-disruptive Overlay-Lösung konzipiert. Es legt sich über Ihren bestehenden Stack und erweitert ihn — kein Rip-and-Replace erforderlich. Ihre aktuellen Tools bleiben unverändert bestehen.',
            },
            {
              q: 'Wie sieht der Onboarding-Prozess aus?',
              a: 'Wir beginnen mit einer 2-wöchigen Discovery-Phase zur Erfassung Ihrer Prozesse und Datenflüsse. Anschließend konfigurieren und optimieren wir die Modelle, führen einen Pilot mit einem Teil Ihres Teams durch und skalieren von dort aus. Sie haben ab Tag eins einen festen Customer Success Manager.',
            },
          ],
        },
        {
          category: 'Sicherheit & Compliance',
          icon: 'ri-shield-check-line',
          items: [
            {
              q: 'Wo werden unsere Daten verarbeitet und gespeichert?',
              a: 'Alle Daten werden ausschließlich in deutschen Rechenzentren (ISO 27001 zertifiziert) verarbeitet und gespeichert. TELIQone ist vollständig DSGVO-konform, und die Datenhoheit verbleibt jederzeit bei Ihnen — wir verwenden Ihre Daten niemals zum Training gemeinsamer Modelle.',
            },
            {
              q: 'Ist TELIQone DSGVO-konform?',
              a: 'Ja. TELIQone wurde von Grund auf für die DSGVO-Konformität entwickelt. Wir bieten einen Auftragsverarbeitungsvertrag (AVV), unterstützen Betroffenenanfragen und führen vollständige Audit-Logs. Unser Rechtsteam ist in Deutschland ansässig.',
            },
            {
              q: 'Wie wird der Zugriff auf sensible Kundendaten kontrolliert?',
              a: 'TELIQone verwendet rollenbasierte Zugriffskontrolle (RBAC), Ende-zu-Ende-Verschlüsselung (TLS 1.3 bei der Übertragung, AES-256 im Ruhezustand) sowie detaillierte Audit-Trails. SSO und MFA werden standardmäßig unterstützt.',
            },
            {
              q: 'Hat TELIQone unabhängige Sicherheitsaudits durchlaufen?',
              a: 'Ja. Wir führen jährliche Penetrationstests durch akkreditierte Drittunternehmen durch und halten die ISO 27001-Zertifizierung aufrecht. Auditberichte sind für Enterprise-Kunden unter NDA verfügbar.',
            },
          ],
        },
        {
          category: 'Preisgestaltung',
          icon: 'ri-price-tag-3-line',
          items: [
            {
              q: 'Wie wird TELIQone berechnet?',
              a: 'TELIQone wird auf Basis eines modularen SaaS-Abonnements berechnet — Sie zahlen für die aktivierten Module und das verarbeitete Volumen. Es gibt keine versteckten Einrichtungsgebühren. Kontaktieren Sie uns für ein maßgeschneidertes Angebot basierend auf Ihrer Agentenanzahl und Ihrem Ticketvolumen.',
            },
            {
              q: 'Gibt es eine Mindestvertragslaufzeit?',
              a: 'Unsere Standardverträge laufen 12 Monate, mit Optionen für 24 oder 36 Monate zu Vorzugskonditionen. Wir bieten auch ein 90-tägiges Pilotprogramm für neue Kunden an, die den ROI vor einer Verpflichtung validieren möchten.',
            },
            {
              q: 'Was ist im Abonnement enthalten?',
              a: 'Alle Abonnements beinhalten Plattformzugang, Modell-Updates, dedizierten Support, SLA-Garantien und Zugang zu unserem Customer-Success-Team. Enterprise-Pläne umfassen zusätzlich individuelle Integrationen, Vor-Ort-Schulungen und einen festen Account Manager.',
            },
            {
              q: 'Können wir mit einem Modul starten und später erweitern?',
              a: 'Absolut. TELIQone ist von Grund auf modular aufgebaut. Die meisten Kunden starten mit Ticket Intelligence oder Process Automation und erweitern auf weitere Module, sobald sie Ergebnisse sehen — ohne jegliche Systemmigration.',
            },
          ],
        },
      ],
      bottomText: 'Noch Fragen?',
      bottomCTA: 'Direkt Kontakt aufnehmen',
    },
    en: {
      badge: 'FAQ',
      headline: 'Frequently Asked Questions',
      subtext: "Everything you need to know about TELIQone's implementation, security, and pricing.",
      categories: [
        {
          category: 'Implementation',
          icon: 'ri-settings-3-line',
          items: [
            {
              q: 'How long does implementation take?',
              a: 'A typical TELIQone rollout takes 6–10 weeks from kickoff to go-live. This includes system integration, process mapping, fine-tuning models on your telco data, and agent training. We provide a dedicated team throughout the entire implementation.',
            },
            {
              q: 'Which systems does TELIQone integrate with?',
              a: 'TELIQone connects natively with leading BSS/OSS platforms (e.g., Amdocs, Comverse, Ericsson), CRM systems (Salesforce, SAP), ticketing tools (ServiceNow, Jira), and any REST/SOAP API. Custom connectors are available on request.',
            },
            {
              q: 'Do we need to replace our existing infrastructure?',
              a: 'No. TELIQone is designed as a non-disruptive overlay solution. It sits on top of your existing stack and extends it — no rip-and-replace required. Your current tools remain unchanged.',
            },
            {
              q: 'What does the onboarding process look like?',
              a: "We start with a 2-week discovery phase to capture your processes and data flows. Then we configure and optimize the models, run a pilot with part of your team, and scale from there. You'll have a dedicated Customer Success Manager from day one.",
            },
          ],
        },
        {
          category: 'Security & Compliance',
          icon: 'ri-shield-check-line',
          items: [
            {
              q: 'Where is our data processed and stored?',
              a: 'All data is processed and stored exclusively in German data centers (ISO 27001 certified). TELIQone is fully GDPR-compliant, and data sovereignty remains with you at all times — we never use your data to train shared models.',
            },
            {
              q: 'Is TELIQone GDPR-compliant?',
              a: 'Yes. TELIQone was built from the ground up for GDPR compliance. We provide a Data Processing Agreement (DPA), support data subject requests, and maintain full audit logs. Our legal team is based in Germany.',
            },
            {
              q: 'How is access to sensitive customer data controlled?',
              a: 'TELIQone uses role-based access control (RBAC), end-to-end encryption (TLS 1.3 in transit, AES-256 at rest), and detailed audit trails. SSO and MFA are supported by default.',
            },
            {
              q: 'Has TELIQone undergone independent security audits?',
              a: 'Yes. We conduct annual penetration tests by accredited third parties and maintain ISO 27001 certification. Audit reports are available for enterprise customers under NDA.',
            },
          ],
        },
        {
          category: 'Pricing',
          icon: 'ri-price-tag-3-line',
          items: [
            {
              q: 'How is TELIQone priced?',
              a: 'TELIQone is priced on a modular SaaS subscription basis — you pay for the activated modules and processed volume. There are no hidden setup fees. Contact us for a tailored quote based on your agent count and ticket volume.',
            },
            {
              q: 'Is there a minimum contract term?',
              a: 'Our standard contracts run 12 months, with options for 24 or 36 months at preferential rates. We also offer a 90-day pilot program for new customers who want to validate ROI before committing.',
            },
            {
              q: "What's included in the subscription?",
              a: 'All subscriptions include platform access, model updates, dedicated support, SLA guarantees, and access to our Customer Success team. Enterprise plans additionally include custom integrations, on-site training, and a dedicated account manager.',
            },
            {
              q: 'Can we start with one module and expand later?',
              a: 'Absolutely. TELIQone is modular by design. Most customers start with Ticket Intelligence or Process Automation and expand to additional modules once they see results — without any system migration.',
            },
          ],
        },
      ],
      bottomText: 'Still have questions?',
      bottomCTA: 'Contact Us Directly',
    },
  };

  const t = content[language as keyof typeof content];

  return (
    <section id="faq" className="py-20 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-xs font-bold tracking-widest uppercase text-black/40 mb-3 block">
            {t.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-black mb-4 leading-tight">
            {t.headline}
          </h2>
          <p className="text-black/50 max-w-xl mx-auto text-base">{t.subtext}</p>
        </div>

        {/* Categories */}
        <div className="space-y-10">
          {t.categories.map((cat, ci) => (
            <div key={ci}>
              {/* Category label */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 flex items-center justify-center bg-black rounded-xl">
                  <i className={`${cat.icon} text-white text-base`}></i>
                </div>
                <h3 className="text-black font-black text-lg tracking-tight">
                  {cat.category}
                </h3>
              </div>

              {/* Items */}
              <div className="border border-black/10 rounded-2xl overflow-hidden divide-y divide-black/10">
                {cat.items.map((item, ii) => {
                  const key = `${ci}-${ii}`;
                  const isOpen = openIndex === key;
                  return (
                    <div key={ii} className="bg-white hover:bg-black/[0.02] transition-colors">
                      <button
                        onClick={() => toggle(key)}
                        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                      >
                        <span className="text-black font-semibold text-sm sm:text-base leading-snug">
                          {item.q}
                        </span>
                        <div
                          className={`w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full border border-black/10 transition-all duration-300 ${
                            isOpen ? 'bg-black border-black rotate-45' : 'bg-transparent'
                          }`}
                        >
                          <i className={`ri-add-line text-sm ${isOpen ? 'text-white' : 'text-black/50'}`}></i>
                        </div>
                      </button>

                      <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                          isOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <p className="px-6 pb-5 text-black/55 text-sm leading-relaxed">
                          {item.a}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center">
          <p className="text-black/50 text-sm mb-4">{t.bottomText}</p>
          <a
            href="mailto:info@teliq.one"
            className="inline-flex items-center gap-2 px-7 py-3 bg-black text-white text-sm font-bold rounded-md hover:bg-black/80 transition-all whitespace-nowrap cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-line"></i>
            </div>
            {t.bottomCTA}
          </a>
        </div>
      </div>
    </section>
  );
}
